// Wire contracts of the per-client AI context layer (ADR-0173 fase 2).
// Every enum derives from the engine (wire-enums-derive-from-engine): the
// engine owns the vocabulary, this file owns request/response shapes.
import { bundleRuleSchema, contextBundleV2Schema, contextExtractionFailureReasonSchema, contextRuleStatusSchema, contextSourceStatusSchema, extractedRuleSchema, missingEvidenceReasonSchema, organizationFeatureFlagKeySchema, ORGANIZATION_FEATURE_FLAG_KEYS, ruleParamSchema, ruleResultStatusSchema, ruleVerificationSchema, } from "@bitacorapro/core-engine";
import { z } from "zod";
// ── Feature flags (PR-1) ─────────────────────────────────────────────────
export { organizationFeatureFlagKeySchema };
/** Every key present, false when no row exists. */
export const organizationFeatureFlagsSchema = z.object(Object.fromEntries(ORGANIZATION_FEATURE_FLAG_KEYS.map((k) => [k, z.boolean()])));
/** PATCH /admin/orgs/:id/feature-flags — platform admin only. Partial:
 *  only the keys sent change; each change is audited one by one. */
export const adminFeatureFlagsPatchBody = z
    .object(Object.fromEntries(ORGANIZATION_FEATURE_FLAG_KEYS.map((k) => [k, z.boolean().optional()])))
    .strict()
    .refine((b) => Object.values(b).some((v) => v !== undefined), {
    message: "at least one flag must be given",
});
export const adminFeatureFlagsResponseSchema = z.object({
    organizationId: z.string().uuid(),
    flags: organizationFeatureFlagsSchema,
});
// ── Context sources (PR-2) ───────────────────────────────────────────────
export { contextSourceStatusSchema, contextRuleStatusSchema, contextExtractionFailureReasonSchema };
const contextSourceKindKeySchema = z.string().regex(/^[a-z][a-z0-9_]{1,31}$/);
/** POST /context-sources/upload-url */
export const contextSourceUploadUrlBody = z
    .object({
    brandId: z.string().uuid(),
    kind: contextSourceKindKeySchema,
    contentType: z.string().min(1).max(120),
    contentLength: z.number().int().positive(),
})
    .strict();
export const contextSourceUploadUrlResponseSchema = z.object({
    uploadUrl: z.string().url(),
    objectKey: z.string().min(1),
    expiresAt: z.string(),
    requiredHeaders: z.record(z.string()),
});
/** POST /context-sources — after the PUT. `sha256` is the client's digest
 *  of the bytes (hex), used for the per-brand duplicate check. */
export const contextSourceCreateBody = z
    .object({
    brandId: z.string().uuid(),
    kind: contextSourceKindKeySchema,
    objectKey: z.string().min(1).max(300),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    fileName: z.string().trim().min(1).max(200).optional(),
    validFrom: z.string().datetime().optional(),
    validTo: z.string().datetime().optional(),
})
    .strict();
export const contextSourceSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    brandId: z.string().uuid(),
    kind: contextSourceKindKeySchema,
    status: contextSourceStatusSchema,
    failureReason: contextExtractionFailureReasonSchema.nullable(),
    fileName: z.string().nullable(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    sha256: z.string(),
    validFrom: z.string().nullable(),
    validTo: z.string().nullable(),
    uploadedByUserId: z.string().uuid(),
    extractionStartedAt: z.string().nullable(),
    activatedAt: z.string().nullable(),
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const contextSourcesListResponseSchema = z.object({
    sources: z.array(contextSourceSchema),
});
// ── /internal/context/* — ai-svc → core-api callbacks (PR-3) ─────────────
// core-api is the only writer of context tables; ai-svc reports through
// these, authenticated with X-Internal-Token (ADR-0033 / ADR-0107).
export const internalContextSourceDownloadUrlBody = z
    .object({ organizationId: z.string().uuid(), sourceId: z.string().uuid() })
    .strict();
export const internalContextSourceDownloadUrlResponseSchema = z.object({
    sourceId: z.string().uuid(),
    kind: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    sha256: z.string(),
    downloadUrl: z.string().url(),
    expiresAt: z.string(),
});
/** The source's native text as the extractor saw it (already masked by
 *  ai-svc; core-api masks again before storing). ≤256 KB total. */
export const nativeTextUnitsSchema = z.object({
    kind: z.enum(["pdf", "xlsx", "image"]),
    units: z
        .array(z.object({
        locator: z.string().min(1).max(120),
        text: z.string().max(20_000),
        hidden: z.boolean().optional(),
    }))
        .max(5_000),
});
export const internalContextDraftRulesBody = z
    .object({
    organizationId: z.string().uuid(),
    sourceId: z.string().uuid(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    extractorVersion: z.string().min(1).max(60),
    model: z.string().max(80).optional(),
    nativeText: nativeTextUnitsSchema,
    rules: z.array(extractedRuleSchema).max(500),
})
    .strict();
export const internalContextDraftRulesResponseSchema = z.object({
    sourceId: z.string().uuid(),
    status: contextSourceStatusSchema,
    rulesCreated: z.number().int().nonnegative(),
    rulesNeedingReview: z.number().int().nonnegative(),
    rulesInConflict: z.number().int().nonnegative(),
    linksDropped: z.number().int().nonnegative(),
});
export const internalContextExtractionFailedBody = z
    .object({
    organizationId: z.string().uuid(),
    sourceId: z.string().uuid(),
    reason: contextExtractionFailureReasonSchema,
})
    .strict();
// ── Review, activation and versions (PR-5) ───────────────────────────────
export { bundleRuleSchema, contextBundleV2Schema, ruleParamSchema, ruleVerificationSchema };
// ── Per-rule results (PR-8b) ─────────────────────────────────────────────
export { missingEvidenceReasonSchema, ruleResultStatusSchema };
/** One rule result as ai-svc hands it to core-api: the rule version the
 *  pinned bundle named, the model's status (or `no_determinable` for a
 *  `dato` / `humano` rule the model never saw), and the citations. Photo
 *  indices are 0-based offsets into the bloque's photo list, audio-note
 *  ids are area_captures.audio_notes[].id — the same contract as the
 *  item-level ai_photo_indices / ai_audio_note_ids. */
export const ruleResultWriteSchema = z
    .object({
    ruleVersionId: z.string().uuid(),
    status: ruleResultStatusSchema,
    observed: z.string().trim().max(300).optional(),
    expected: z.string().trim().max(300).optional(),
    missingEvidence: missingEvidenceReasonSchema.optional(),
    photoIndices: z.array(z.number().int().nonnegative()).max(20).optional(),
    audioNoteIds: z.array(z.string().min(1)).max(20).optional(),
})
    .strict();
/** POST /internal/context/rule-results — the whole bloque in one call,
 *  AFTER the item upsert ai-svc already did (the row each result hangs
 *  off must exist). core-api checks every ruleVersionId against the
 *  bundle the report pinned (409 otherwise), refuses a terminal report,
 *  and replaces the bloque's rows for those items. */
export const internalContextRuleResultsBody = z
    .object({
    organizationId: z.string().uuid(),
    reportId: z.string().uuid(),
    inspectionAreaId: z.string().uuid(),
    items: z
        .array(z
        .object({
        inspectionChecklistItemId: z.string().uuid(),
        ruleResults: z.array(ruleResultWriteSchema).max(50),
    })
        .strict())
        .min(1)
        .max(200),
})
    .strict();
export const internalContextRuleResultsResponseSchema = z.object({
    reportId: z.string().uuid(),
    rowsWritten: z.number().int().nonnegative(),
    /** Items in the body with no report_checklist_results row yet (ai-svc's
     *  own upsert lost the terminal race, or never ran) — skipped. */
    itemsSkipped: z.number().int().nonnegative(),
});
/** One rule as the reviewer sees it: the rule row + its CURRENT version +
 *  the item codes it targets. Versions are immutable; `version.version`
 *  counts the edits (1 = the extractor's draft). */
export const contextRuleViewSchema = z.object({
    id: z.string().uuid(),
    sourceId: z.string().uuid(),
    brandId: z.string().uuid(),
    status: contextRuleStatusSchema,
    itemCodes: z.array(z.string()),
    version: z.object({
        id: z.string().uuid(),
        version: z.number().int().min(1),
        text: z.string(),
        sourceQuote: z.string().nullable(),
        sourceLocator: z.unknown().nullable(),
        params: z.array(ruleParamSchema),
        responsible: z.string(),
        verification: ruleVerificationSchema,
        verificationNeeds: z.array(z.string()).nullable(),
        stockConditioned: z.boolean(),
        kind: z.string().nullable(),
        flags: z.array(z.string()),
        linkConfidence: z.string().nullable(),
        /** null = the extractor authored it. */
        authoredByUserId: z.string().uuid().nullable(),
        createdAt: z.string(),
    }),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const contextRulesListResponseSchema = z.object({
    sourceId: z.string().uuid(),
    rules: z.array(contextRuleViewSchema),
    /** Summary the panel uses for «lista para activar». */
    counts: z.object({
        total: z.number().int().nonnegative(),
        inConflict: z.number().int().nonnegative(),
        needingReview: z.number().int().nonnegative(),
        discarded: z.number().int().nonnegative(),
        withoutItems: z.number().int().nonnegative(),
    }),
});
/** GET /context-sources/:id/text — the masked native text, for quote highlighting. */
export const contextSourceTextResponseSchema = z.object({
    sourceId: z.string().uuid(),
    nativeText: nativeTextUnitsSchema,
    extractorVersion: z.string(),
    model: z.string().nullable(),
    redactedAt: z.string().nullable(),
});
/** PATCH /context-rules/:id — every field optional; whatever is sent
 *  becomes version v+1 together with the untouched fields of v. */
export const contextRulePatchBody = z
    .object({
    text: z.string().trim().min(1).max(600).optional(),
    params: z.array(ruleParamSchema).max(6).optional(),
    responsible: z
        .string()
        .trim()
        .regex(/^[a-z][a-z0-9_]{1,31}$/)
        .optional(),
    verification: ruleVerificationSchema.optional(),
    stockConditioned: z.boolean().optional(),
    itemCodes: z.array(z.string().trim().min(1).max(32)).max(5).optional(),
})
    .strict()
    .refine((b) => Object.values(b).some((v) => v !== undefined), {
    message: "at least one field must be given",
});
/** POST /context-rules/:id/resolve-conflict — accept this rule, discard
 *  the listed ones (the other members of its conflict group), atomically. */
export const contextRuleResolveConflictBody = z
    .object({ discardRuleIds: z.array(z.string().uuid()).min(1).max(50) })
    .strict();
/** A slot two accepted rules fight for — the body of a 409 CONTEXT_SLOT_CONFLICT. */
export const contextSlotConflictSchema = z.object({
    itemCode: z.string(),
    param: z.string(),
    scope: z.string().nullable(),
    ruleIds: z.array(z.string().uuid()),
});
/** POST /context-sources/:id/activate */
export const contextSourceActivateBody = z
    .object({ label: z.string().trim().min(1).max(80).optional() })
    .strict()
    .optional();
/** A context version on the wire — never the nonce. */
export const contextVersionSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    brandId: z.string().uuid(),
    checklistTemplateId: z.string().uuid().nullable(),
    label: z.string().nullable(),
    status: z.enum(["active", "archived"]),
    bundleSchemaVersion: z.number().int(),
    contentSha256: z.string(),
    sourceIds: z.array(z.string().uuid()),
    ruleCount: z.number().int().nonnegative(),
    supersedesVersionId: z.string().uuid().nullable(),
    activatedByUserId: z.string().uuid(),
    activatedAt: z.string(),
    archivedAt: z.string().nullable(),
    /** PR-9 — open actas still pinned to this version (they keep it after
     *  an archive, D3). Only GET /context-versions computes it. */
    openReportCount: z.number().int().nonnegative().optional(),
});
export const contextVersionDetailSchema = contextVersionSchema.extend({
    bundle: contextBundleV2Schema,
});
export const contextVersionsListResponseSchema = z.object({
    versions: z.array(contextVersionSchema),
});
/** POST /context-sources/:id/activate | archive → the source + the version
 *  the brand now runs (null when archiving left the brand without one). */
export const contextSourceActivationResponseSchema = z.object({
    source: contextSourceSchema,
    version: contextVersionSchema.nullable(),
    /** Rules that were accepted but target no item: carried nowhere. */
    rulesWithoutItems: z.number().int().nonnegative(),
});
/** GET /context-versions/:id/diff?against= — by ruleId. */
export const contextVersionDiffResponseSchema = z.object({
    versionId: z.string().uuid(),
    againstVersionId: z.string().uuid(),
    added: z.array(bundleRuleSchema),
    removed: z.array(bundleRuleSchema),
    changed: z.array(z.object({ ruleId: z.string().uuid(), before: bundleRuleSchema, after: bundleRuleSchema })),
});
// ── Frozen into the acta (PR-9) ──────────────────────────────────────────
/** reports.context_version_snapshot — the pinned version's identity,
 *  frozen at sign/close. `brandName` by value so a later rename does not
 *  change what the acta says. */
export const frozenContextVersionSchema = z.object({
    id: z.string().uuid(),
    contentSha256: z.string().regex(/^[0-9a-f]{64}$/),
    brandId: z.string().uuid(),
    brandName: z.string().nullable(),
    activatedAt: z.string(),
});
// ── Redaction (PR-11) ────────────────────────────────────────────────────
/** POST /admin/orgs/:id/context/redact — exactly one target. */
export const adminContextRedactBody = z.union([
    z.object({ ruleVersionId: z.string().uuid() }).strict(),
    z.object({ sourceId: z.string().uuid() }).strict(),
]);
export const adminContextRedactResponseSchema = z.union([
    z.object({
        organizationId: z.string().uuid(),
        target: z.literal("rule_version"),
        versionsTouched: z.array(z.string().uuid()),
    }),
    z.object({
        organizationId: z.string().uuid(),
        target: z.literal("source_text"),
        unitsRedacted: z.number().int().nonnegative(),
    }),
]);
//# sourceMappingURL=context.js.map